<template>
	<v-container class="fill-height">
		<v-row justify="space-around">
			<v-col cols="4" class="text-center">
				<v-progress-circular v-show="loading" indeterminate color="primary"></v-progress-circular> <br />
				<span>Connecting Ply & {{ humanize(serviceSlug) }}..</span> <br />
			</v-col>
		</v-row>
	</v-container>
</template>

<script>
import { mapGetters } from 'vuex';
import api from '@/api';
import BrowserService from '@/utils/BrowserService';
import AppService from '@/utils/AppService';

// These keys originate from the server
const ORIGINAL_STATE_KEY = 'original_state';
const VERSION_KEY = 'version';

function unpackState(stateString) {
	try {
		let state;
		try {
			state = JSON.parse(atob(stateString));
		} catch (e) {
			console.error('Exception during parsing auth state');
			throw e;
		}
		if (!state || !(ORIGINAL_STATE_KEY in state && VERSION_KEY in state)) {
			throw new Error('Invalid state parameter');
		}
		return {
			version: state[VERSION_KEY],
			state: state[ORIGINAL_STATE_KEY],
		};
	} catch (e) {
		console.error('State unpack error:', e);
		return {
			version: '1.0.0',
			state: '',
		};
	}
}

function slug2Title(slug) {
	var words = slug.split('-');
	return words.map((word) => word.charAt(0).toUpperCase() + word.substring(1).toLowerCase()).join(' ');
}

export default {
	name: 'ConnectService',
	props: {
		serviceSlug: {
			type: String,
			required: true,
		},
		code: {
			type: String,
			required: false,
		},
		error: {
			type: String,
			required: false,
		},
		standalone: {
			type: Boolean,
			required: false,
		},
	},
	computed: {
		...mapGetters('main', ['appURL']),
		chromeExtensionID() {
			return process.env.VUE_APP_CHROME_EXTENSION_ID;
		},
	},
	data: () => ({
		loading: false,
	}),
	methods: {
		humanize(slug) {
			return slug2Title(slug);
		},
	},
	async mounted() {
		if (this.error) {
			if (window.opener) {
				window.opener.postMessage({ error: this.error }, this.appURL);
			}
			if (window.chrome && window.chrome.runtime) {
				BrowserService.sendToExtensions({
					action: 'profileConnection',
					success: 0,
					error: this.error,
					profileConnection: true,
				});
			}
			return;
		}
		if (this.serviceSlug) {
			// we're going directly to the api because this is special view, just as middle man to process callback from the connected service before sending it to server
			this.loading = true;

			// extract service version from "state" query param, and pass on the original oauth state
			const { version, state } = unpackState(this.$route.query.state);

			const ret = await api
				.connectService(this.serviceSlug, { ...this.$route.query, state }, version)
				.catch((err) => {
					const errorResponse = err.response && err.response.data;
					// console.log(`errorResponse is: `, errorResponse);
					if (errorResponse.error && errorResponse.error.msg) {
						this.$toast.error(`<b>Error</b> while connecting <i>${this.serviceSlug}</i>:<br /><b>${errorResponse.error.msg}</b>`, {
							timeout: 0,
							multiLine: true,
							dismissable: true,
						});
					}
				})
				.finally(() => {
					this.loading = false;
				});
			if (!ret) {
				throw new Error('Cant connect service');
			}

			// incase ret is not an object-type instance, run the JS Object class hasOwnProperty
			// see eslint rule 'no-prototype-builtins'
			if (Object.prototype.hasOwnProperty.call(ret, 'connection_name')) {
				if (window.chrome && window.chrome.runtime) {
					BrowserService.sendToExtensions({
						action: 'profileConnection',
						success: 1,
						ret,
						profileConnection: true,
					});
				}

				if (window.opener) {
					window.opener.postMessage({ action: 'profileConnection', success: 1, ret }, '*');
				}

				if (AppService.isSlugOfShopify(this.serviceSlug) || this.standalone) {
					this.$router.push({
						name: 'IntegrationConnected',
						params: {
							serviceSlug: this.serviceSlug,
						},
					});
				} else if (window.opener) {
					// Auto close.
					setTimeout(() => {
						window.close();
					}, 5500);
				}
			} else if (ret.error) {
				alert(ret.errorMessage);
			}
		}
	},
};
</script>

<style scoped></style>
